import { useEffect, useMemo, useRef, useState } from "react";
import { normalizeAccountNumber } from "../../components/portfolio/columns/formatters";
import { useAuth } from "../../contexts/AuthContext";
import { useAccountSummary } from "../../hooks/useAccountData";
import { useAnalyzerRiskAnalysis } from "../../hooks/useAnalyzerRiskAnalysis";
import formService from "../../services/formService";
import type { FactorEntry } from "../../services/productFactorsService";
import { productSearchService } from "../../services/productSearchService";
import type { CumulativeSeriesPoint } from "../../components/performanceChartV3Utils";
import {
  buildAlphaInvestmentValueSeries,
  loadAlphaManagedStrategyDataByProductId,
  type AlphaInvestmentValuePoint,
  type AlphaManagedStrategyPerformanceData,
} from "./alphaRecommendationData";
import type { AlphaIbkrAccountType } from "./alphaIbkrAccountDetails";
import { resolveAlphaManagedMatch } from "./alphaManagedMatch";
import { getAlphaManagedStrategyByMixId } from "./alphaManagedStrategyResolver";
import {
  type AlphaProgressResponses,
} from "./alphaProgressResponses";
import {
  getAlphaMixFloorUsd,
  resolveAlphaRiskTrack,
  type AlphaRiskKey,
  type AlphaRiskTrack,
} from "./alphaRiskMap";
import { getAlphaConfigAccountType } from "./alphaRiskProfile";

const PERCENT_RETURN_BASE = 100;
const PLUTUS_MANAGED_TITLE = "Plutus Managed";
export const ALPHA_WHY_MANAGED_LIVE_ACCOUNT_THRESHOLD_USD = 40_000;

// Stable empty-factors reference keeps useAnalyzerRiskAnalysis memo-stable before
// the managed strategy data has loaded.
const EMPTY_FACTORS: FactorEntry[] = [];

export type AlphaWhyManagedStatus = "loading" | "ready" | "unavailable" | "needsProfile";

// Thrown only when saved answers cannot produce a match. Callers must nudge
// for preferences instead of showing a generic retry. Catalog/network failures
// stay ordinary Errors.
export class AlphaManagedMatchProfileError extends Error {
  constructor() {
    super("No matched Plutus Managed strategy is available for this profile.");
    this.name = "AlphaManagedMatchProfileError";
  }
}

export function isAlphaManagedMatchProfileError(error: unknown): boolean {
  return error instanceof Error && error.name === "AlphaManagedMatchProfileError";
}

// Retained for callers that already hold a complete progress object. Saved
// response matching uses resolveAlphaManagedMatch directly.
export function isAlphaManagedProfileMatchable(
  progress: Pick<
    AlphaProgressResponses,
    "investmentTrackPreference" | "ibkrAccountType" | "riskProfile"
  >,
  accountTypeOverride: AlphaIbkrAccountType | null = null
): boolean {
  const accountType = accountTypeOverride ?? progress.ibkrAccountType;
  const preference = progress.investmentTrackPreference;
  const riskProfile = progress.riskProfile;
  if (
    !preference
    || !accountType
    || !riskProfile
    || riskProfile.investmentTrack !== preference
  ) {
    return false;
  }

  try {
    const amount = Number(riskProfile.plutusInvestment);
    const floorUsd = getAlphaMixFloorUsd(
      resolveAlphaRiskTrack(preference, accountType),
      riskProfile.riskScore
    );
    return Number.isFinite(amount) && amount >= floorUsd;
  } catch {
    return false;
  }
}

export interface AlphaWhyManagedViewModel {
  status: AlphaWhyManagedStatus;
  managedStrategyId: number | null;
  productLabel: string;
  recommendationAmountUsd: number | null;
  recommendationAmountSource: "live" | "questionnaire" | null;
  riskLabel: string;
  accountTypeLabel: string;
  strategyReturnPct: number | null;
  benchmarkReturnPct: number | null;
  maxDrawdown: number | null;
  benchmarkMaxDrawdown: number | null;
  volatility: number | null;
  benchmarkVolatility: number | null;
  constituents: ReadonlyArray<{
    name: string;
    weightPct: number;
  }>;
  strategySeries: CumulativeSeriesPoint[];
  benchmarkSeries: CumulativeSeriesPoint[];
}

export interface AlphaWhyManagedLiveAccountContext {
  isBeta: boolean;
  isEligibleCustomer: boolean;
  hasConnectedAccount: boolean;
  liveAccountValue: number | null;
}

// Only a connected beta customer above the strict threshold uses live NLV.
// All other states deliberately return null so the saved questionnaire amount
// remains the resolver input. NLV is rounded to whole dollars because the
// existing Alpha amount-band resolver accepts whole USD values.
export function getAlphaWhyManagedLiveAmountOverride(
  context: AlphaWhyManagedLiveAccountContext
): number | null {
  if (
    !context.isBeta
    || !context.isEligibleCustomer
    || !context.hasConnectedAccount
    || context.liveAccountValue === null
    || !Number.isFinite(context.liveAccountValue)
    || context.liveAccountValue <= ALPHA_WHY_MANAGED_LIVE_ACCOUNT_THRESHOLD_USD
  ) {
    return null;
  }
  return Math.round(context.liveAccountValue);
}

export interface AlphaMatchedManagedRecommendation {
  managedStrategyId: number;
  investmentAmountUsd: number;
  riskKey: AlphaRiskKey;
  riskTrack: AlphaRiskTrack;
}

// Resolve the 100%-Managed recommendation from the saved investment track,
// risk score, and account type. A live amount override changes only the sizing
// input; null preserves the questionnaire amount and every existing caller.
export async function resolveMatchedManagedRecommendation(
  signal?: AbortSignal,
  accountTypeOverride: AlphaIbkrAccountType | null = null,
  investmentAmountOverrideUsd: number | null = null
): Promise<AlphaMatchedManagedRecommendation> {
  const [questions, responses] = await Promise.all([
    formService.listQuestions(),
    formService.getMyResponses(),
  ]);
  const match = investmentAmountOverrideUsd === null
    ? resolveAlphaManagedMatch(questions, responses, accountTypeOverride)
    : resolveAlphaManagedMatch(
      questions,
      responses,
      accountTypeOverride,
      investmentAmountOverrideUsd
    );
  if (match.status !== "ready") {
    throw new AlphaManagedMatchProfileError();
  }
  const managed = await getAlphaManagedStrategyByMixId(
    match.selection.mixId,
    signal
  );
  return {
    managedStrategyId: managed.productId,
    investmentAmountUsd: match.investmentAmountUsd,
    riskKey: match.riskKey,
    riskTrack: match.selection.riskTrack,
  };
}

// Existing allocation-editor actions keep their questionnaire-sized behavior.
export async function resolveMatchedManagedStrategyId(
  signal?: AbortSignal,
  accountTypeOverride: AlphaIbkrAccountType | null = null
): Promise<number> {
  const recommendation = await resolveMatchedManagedRecommendation(
    signal,
    accountTypeOverride
  );
  return recommendation.managedStrategyId;
}

export type AlphaRiskProfilePresence = "loading" | "present" | "missing";

// Whether the signed-in investor's four saved answers map to a managed strategy.
// Race-guarded so a slower earlier load never overwrites a newer one. On load
// failure we assume "present" so a form-service outage does not nag; callers
// must still treat a later AlphaManagedMatchProfileError as "missing".
export function useAlphaRiskProfilePresence(
  active: boolean,
  accountTypeOverride: AlphaIbkrAccountType | null = null
): AlphaRiskProfilePresence {
  const [presence, setPresence] = useState<AlphaRiskProfilePresence>("loading");
  const requestRef = useRef(0);

  useEffect(() => {
    if (!active) {
      setPresence("loading");
      return;
    }

    const requestId = ++requestRef.current;
    setPresence("loading");

    void (async () => {
      try {
        const [questions, responses] = await Promise.all([
          formService.listQuestions(),
          formService.getMyResponses(),
        ]);
        if (requestId !== requestRef.current) return;
        const match = resolveAlphaManagedMatch(
          questions,
          responses,
          accountTypeOverride
        );
        setPresence(match.status === "ready" ? "present" : "missing");
      } catch {
        if (requestId !== requestRef.current) return;
        setPresence("present");
      }
    })();
  }, [active, accountTypeOverride]);

  return presence;
}

function toCumulativeSeries(
  points: AlphaInvestmentValuePoint[]
): CumulativeSeriesPoint[] {
  return points.map((point) => {
    const iso = new Date(point.timestamp).toISOString();
    return {
      date: iso,
      fullDate: iso,
      performance: (point.value / PERCENT_RETURN_BASE - 1) * 100,
    };
  });
}

type LoadState = "idle" | "loading" | "loaded" | "failed" | "needsProfile";

interface AlphaWhyManagedRecommendationData {
  recommendation: AlphaMatchedManagedRecommendation;
  recommendationAmountSource: "live" | "questionnaire";
}

interface AlphaWhyManagedPresentationData {
  performance: AlphaManagedStrategyPerformanceData;
  constituents: Array<{ name: string; weightPct: number }>;
}

// Loads one profile-matched 100%-Managed recommendation. A held partial
// strategy is intentionally not an input: this explainer always models the
// whole-account recommendation. Async writes are token-guarded and aborted.
export function useAlphaWhyManagedData(active: boolean): AlphaWhyManagedViewModel {
  const {
    getUserConfigValue,
    userConfig,
    userConfigLoaded,
  } = useAuth();
  const configAccountType = getAlphaConfigAccountType(userConfig?.data);
  const isBeta = userConfigLoaded && getUserConfigValue("isbeta") === "true";
  const isEligibleCustomer = userConfigLoaded
    && getUserConfigValue("iscustomer") === "true";
  const hasConnectedAccount = Boolean(userConfig?.ibkrAccountId);
  const canUseLiveAccount = Boolean(
    isBeta && isEligibleCustomer && hasConnectedAccount
  );
  const accountSummary = useAccountSummary(
    undefined,
    active && canUseLiveAccount
  );
  const liveAccountValue = accountSummary.error
    ? null
    : normalizeAccountNumber(accountSummary.data?.netLiquidationSecurity);
  const liveAmountOverrideUsd = getAlphaWhyManagedLiveAmountOverride({
    isBeta,
    isEligibleCustomer,
    hasConnectedAccount,
    liveAccountValue,
  });
  const waitingForLiveAccount = active
    && canUseLiveAccount
    && accountSummary.isLoading;
  const [recommendationData, setRecommendationData] =
    useState<AlphaWhyManagedRecommendationData | null>(null);
  const [presentationData, setPresentationData] =
    useState<AlphaWhyManagedPresentationData | null>(null);
  const [loadState, setLoadState] = useState<LoadState>("idle");
  const requestRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestRef.current;
    if (!active) {
      setLoadState("idle");
      setRecommendationData(null);
      setPresentationData(null);
      return undefined;
    }
    if (waitingForLiveAccount) {
      setLoadState("loading");
      setRecommendationData(null);
      setPresentationData(null);
      return undefined;
    }

    const controller = new AbortController();
    setLoadState("loading");
    setRecommendationData(null);
    setPresentationData(null);

    void (async () => {
      let recommendation: AlphaMatchedManagedRecommendation;
      try {
        recommendation = await resolveMatchedManagedRecommendation(
          controller.signal,
          configAccountType,
          liveAmountOverrideUsd
        );
      } catch (error) {
        if (requestId !== requestRef.current || controller.signal.aborted) return;
        setRecommendationData(null);
        setPresentationData(null);
        setLoadState(
          isAlphaManagedMatchProfileError(error) ? "needsProfile" : "failed"
        );
        return;
      }

      if (requestId !== requestRef.current || controller.signal.aborted) return;
      setRecommendationData({
        recommendation,
        recommendationAmountSource: liveAmountOverrideUsd === null
          ? "questionnaire"
          : "live",
      });

      try {
        const performance = await loadAlphaManagedStrategyDataByProductId(
          recommendation.managedStrategyId,
          controller.signal
        );
        const constituents = await Promise.all(performance.composition.map(async (entry) => {
          const product = await productSearchService.getProductForInvestor(
            entry.entityType,
            entry.productId,
            controller.signal
          );
          const name = product.title.trim();
          if (!name) {
            throw new Error("A recommended constituent has no display name.");
          }
          return { name, weightPct: entry.weightPct };
        }));
        if (requestId !== requestRef.current || controller.signal.aborted) return;
        setPresentationData({
          performance,
          constituents,
        });
        setLoadState("loaded");
      } catch {
        if (requestId !== requestRef.current || controller.signal.aborted) return;
        // Recommendation identity is authoritative and remains adoptable even
        // when optional chart or constituent presentation data is unavailable.
        setPresentationData(null);
        setLoadState("failed");
      }
    })();

    return () => controller.abort();
  }, [
    active,
    configAccountType,
    liveAmountOverrideUsd,
    waitingForLiveAccount,
  ]);

  const performance = presentationData?.performance ?? null;

  const { metrics, benchmarkMetrics } = useAnalyzerRiskAnalysis(
    performance?.strategyFactors ?? EMPTY_FACTORS,
    performance?.benchmarkFactors ?? EMPTY_FACTORS
  );

  const strategySeries = useMemo(
    () => (performance
      ? toCumulativeSeries(
        buildAlphaInvestmentValueSeries(
          performance.strategyFactors,
          performance.window,
          PERCENT_RETURN_BASE
        )
      )
      : []),
    [performance]
  );
  const benchmarkSeries = useMemo(
    () => (performance
      ? toCumulativeSeries(
        buildAlphaInvestmentValueSeries(
          performance.benchmarkFactors,
          performance.window,
          PERCENT_RETURN_BASE
        )
      )
      : []),
    [performance]
  );

  return useMemo<AlphaWhyManagedViewModel>(() => {
    const isReady = loadState === "loaded"
      && presentationData !== null
      && strategySeries.length > 1
      && benchmarkSeries.length > 1
      && metrics !== null
      && benchmarkMetrics !== null;
    const status: AlphaWhyManagedStatus = loadState === "loading" || loadState === "idle"
      ? "loading"
      : loadState === "needsProfile"
        ? "needsProfile"
        : isReady ? "ready" : "unavailable";

    return {
      status,
      managedStrategyId: recommendationData?.recommendation.managedStrategyId ?? null,
      productLabel: PLUTUS_MANAGED_TITLE,
      recommendationAmountUsd: recommendationData?.recommendation.investmentAmountUsd ?? null,
      recommendationAmountSource: recommendationData?.recommendationAmountSource ?? null,
      riskLabel: recommendationData
        ? `${recommendationData.recommendation.riskKey}/10`
        : "--",
      accountTypeLabel: recommendationData?.recommendation.riskTrack.startsWith("Retirement")
        ? "Retirement"
        : recommendationData ? "Taxable" : "--",
      strategyReturnPct: performance?.strategyReturnPct ?? null,
      benchmarkReturnPct: performance?.benchmarkReturnPct ?? null,
      maxDrawdown: metrics?.maxDrawdown ?? null,
      benchmarkMaxDrawdown: benchmarkMetrics?.maxDrawdown ?? null,
      volatility: metrics?.volatility ?? null,
      benchmarkVolatility: benchmarkMetrics?.volatility ?? null,
      constituents: presentationData?.constituents ?? [],
      strategySeries,
      benchmarkSeries,
    };
  }, [
    benchmarkMetrics,
    benchmarkSeries,
    loadState,
    metrics,
    performance,
    presentationData,
    recommendationData,
    strategySeries,
  ]);
}
